import React, { useState, useEffect } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Box,
  Stack,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/HelpOutlined';
import BookmarkIcon from '@mui/icons-material/BookmarkBorderOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useClerk } from '@clerk/clerk-react';
import { IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import tenantService, { TENANT_LOGO_UPDATED_EVENT } from '../services/tenantService';
import { useUserStore } from '../store/userStore';
import { useUserContext } from '../hooks/useUserContext';
import { config } from '../config';
import CpaxLogo from './CpaxLogo';

interface AppBarProps {
  /**
   * User initials for avatar
   */
  userInitials?: string;
}

export default function AppBar({
  userInitials = 'OP',
}: AppBarProps) {
  const userState = useUserStore();
  const { signOut, openUserProfile } = useClerk();
  const navigate = useNavigate();
  const { isTenantAdmin, backendProfile } = useUserContext();
  const isOrgMember = !!backendProfile?.tenantId && backendProfile.tenantId !== config.defaultTenantId;

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [tenantLogoUrl, setTenantLogoUrl] = useState<string | null>(null);

  // Load tenant logo and refresh when a new tenant logo is uploaded.
  useEffect(() => {
    let isMounted = true;

    const loadTenantLogo = async (tenantIdFromEvent?: string) => {
      try {
        const resolvedTenantId = tenantIdFromEvent || backendProfile?.tenantId;
        // Only load a custom logo for actual org members, not the default tenant
        if (!resolvedTenantId || resolvedTenantId === config.defaultTenantId) {
          if (isMounted) setTenantLogoUrl(null);
          return;
        }
        const logoUrl = await tenantService.getTenantLogo(resolvedTenantId);

        if (!isMounted) return;
        setTenantLogoUrl(logoUrl || null);
      } catch {
        if (isMounted) {
          setTenantLogoUrl(null);
        }
      }
    };

    const handleTenantLogoUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ tenantId?: string }>;
      void loadTenantLogo(customEvent.detail?.tenantId);
    };

    void loadTenantLogo();
    window.addEventListener(TENANT_LOGO_UPDATED_EVENT, handleTenantLogoUpdated as EventListener);

    return () => {
      isMounted = false;
      window.removeEventListener(TENANT_LOGO_UPDATED_EVENT, handleTenantLogoUpdated as EventListener);
    };
  }, [backendProfile?.tenantId]);

  // Get user data from centralized store
  const user = userState.clerkUser;
  
  // Get user initials from Clerk user data if available
  const displayInitials = user
    ? `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase()
    : userInitials;

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfile = () => {
    handleUserMenuClose();
    navigate('/app/profile');
  };

  const handleSettings = () => {
    handleUserMenuClose();
    // Hide Clerk's user-facing API Keys page; keys are dev-only and managed in the Clerk Dashboard
    openUserProfile({ apiKeysProps: { hide: true } });
  };

  const handleBilling = () => {
    handleUserMenuClose();
    navigate(isTenantAdmin ? '/app/tenant-admin/billing' : '/app/billing');
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await signOut();
    navigate('/');
  };

  const handleHelpClick = () => {
    navigate('/app/faq');
  };

  const handleBookmarksClick = () => {
    navigate('/app/bookmarks');
  };

  const displayFullName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User';

  return (
    <Box>
      <MuiAppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar
          sx={{
            minHeight: '64px !important',
            height: 64,
            px: 3, // 24px
          }}
        >
          {/* Left Side: Logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', py: 1, px: 1 }}>
            <CpaxLogo height={36} />
          </Box>

          {/* Right Side: Notifications, Bookmark, Help, Stats, User */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 2 }} alignItems="center">
            {/* Bookmark Icon — hidden on mobile */}
            <Tooltip title="Bookmarks" arrow>
              <IconButton
                onClick={handleBookmarksClick}
                aria-label="View bookmarks"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: 'action.active',
                  p: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                  },
                }}
              >
                <BookmarkIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>

            {/* Help Icon — hidden on mobile */}
            <Tooltip title="Help & FAQ" arrow>
              <IconButton
                onClick={handleHelpClick}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: 'action.active',
                  p: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                }}
                aria-label="Open help and frequently asked questions"
              >
                <HelpIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>
            {/* User Avatar Badge */}
            <Tooltip
              title={displayFullName}
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(97, 97, 97, 0.92)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    letterSpacing: '0.17px',
                    py: 0.5,
                    px: 1,
                    borderRadius: 0.5,
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(97, 97, 97, 0.92)',
                  },
                },
              }}
            >
              <Box
                onClick={handleUserMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25, // 10px
                  px: 1.5, // 12px
                  py: 1, // 8px
                  border: '1px solid',
                  borderColor: 'divider', // rgba(0,0,0,0.12)
                  borderRadius: '48px',
                  height: '46px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
              {/* Tenant / CPAx nav icon */}
              {tenantLogoUrl ? (
                <Box
                  component="img"
                  src={tenantLogoUrl}
                  alt="Tenant Logo"
                  sx={{
                    height: '28px',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src="/favicon-32x32.png"
                  alt="CPAx"
                  sx={{
                    width: 28,
                    height: 28,
                    objectFit: 'contain',
                    flexShrink: 0,
                  }}
                />
              )}
              
              {/* User Avatar — relies on theme's MuiAvatar default styling
                  (primary bg + white text). Previous explicit `#bdbdbd` bg with
                  white text gave ~1.6:1 contrast (failed WCAG AA). */}
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {displayInitials}
              </Avatar>
              </Box>
            </Tooltip>
          </Stack>
        </Toolbar>

        {/* Bottom Divider */}
        <Divider />
      </MuiAppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 1,
          '& .MuiPaper-root': {
            bgcolor: 'background.paper',
            borderRadius: 1, // 4px
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
            minWidth: 200,
          },
          '& .MuiList-root': {
            py: 1, // 8px
          },
        }}
      >
        <MenuItem 
          onClick={handleProfile}
          sx={{
            px: 2, // 16px
            py: 1.5, // 12px
            minHeight: 48,
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: 'action.active',
            }}
          >
            <AccountCircleIcon 
              sx={{ 
                fontSize: 24,
                color: 'action.active',
              }} 
            />
          </ListItemIcon>
          <ListItemText
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.15px',
                color: 'text.primary',
              },
            }}
          >
            Profile
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleSettings}
          sx={{
            px: 2, // 16px
            py: 1.5, // 12px
            minHeight: 48,
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: 'action.active',
            }}
          >
            <SettingsIcon 
              sx={{ 
                fontSize: 24,
                color: 'action.active',
              }} 
            />
          </ListItemIcon>
          <ListItemText
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.15px',
                color: 'text.primary',
              },
            }}
          >
            Settings
          </ListItemText>
        </MenuItem>
        {(!isOrgMember || isTenantAdmin) && (
          <MenuItem
            onClick={handleBilling}
            sx={{
              px: 2, // 16px
              py: 1.5, // 12px
              minHeight: 48,
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: 'action.active',
              }}
            >
              <CreditCardIcon
                sx={{
                  fontSize: 24,
                  color: 'action.active',
                }}
              />
            </ListItemIcon>
            <ListItemText
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  letterSpacing: '0.15px',
                  color: 'text.primary',
                },
              }}
            >
              Billing
            </ListItemText>
          </MenuItem>
        )}
        <Divider
          sx={{
            my: 1, // 8px vertical margin
            borderColor: 'divider', // rgba(0,0,0,0.12)
          }}
        />
        <MenuItem
          onClick={handleLogout}
          sx={{
            px: 2, // 16px
            py: 1.5, // 12px
            minHeight: 48,
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: 'error.main',
            }}
          >
            <LogoutIcon 
              sx={{ 
                fontSize: 24,
                color: 'error.main',
              }} 
            />
          </ListItemIcon>
          <ListItemText
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.5,
                letterSpacing: '0.15px',
                color: 'error.main',
              },
            }}
          >
            Logout
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
